import {
  AutoComplete,
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  fetchBranchMembers,
  fetchRolePermissions,
  fetchUserPermissions,
  enforceRoleOnMembers,
  updateMemberDeltas,
  updateRolePermissions,
} from "../../../apis/branch.api";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  createPermission,
  deactivatePermission,
  fetchPermissionCatalog,
  updatePermission,
} from "../../../apis/branch.api";

// The CRUD vocabulary (not permission data — just which actions form the grid columns).
const CORE_ACTIONS = ["create", "read", "update", "delete"] as const;

const TEAL = "#24AAB7";
const GREEN = "#389e0d";
const RED = "#cf1322";

const ROLE_DISPLAY: Record<string, string> = {
  customer_success: "consultants",
  retail: "receptionist",
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const memberRole = (m: any): string | null =>
  m?.user_permission_detail?.permission_name ?? null;
const roleLabel = (r: string | null): string | null =>
  r ? ROLE_DISPLAY[r] ?? r : null;
const memberName = (m: any): string => {
  const name = `${m?.user?.f_name ?? ""} ${m?.user?.l_name ?? ""}`.trim();
  return name || m?.user?.email || `User #${m?.id}`;
};
const initials = (m: any): string => {
  const p = memberName(m).split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
};
const setsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && Array.from(a).every((x) => b.has(x));

// -------------------------------------------------------------------------
// Catalogue → grouped rows (Category → Module → permission rows). Presentation
// only; the set of slugs still comes from the local catalogue (developer-defined
// vocabulary), while role membership + per-user deltas come from the server.
// -------------------------------------------------------------------------
interface PermRow {
  slug: string;
  label: string;
  description?: string;
}
interface ModuleGroup {
  key: string;
  label: string;
  // CRUD slugs by action (a module is a "grid" row only when all four exist).
  core: Partial<Record<string, PermRow>>;
  // Everything module-specific (non-CRUD) — shown under the row's expander.
  extras: PermRow[];
  isGrid: boolean;
}
interface CategoryGroup {
  key: string;
  label: string;
  modules: ModuleGroup[];
}

// A raw catalogue row as returned by GET /permissions/catalog/.
interface CatalogRow {
  slug: string;
  entity: string;
  action: string;
  label: string;
  description?: string;
  category: string;
  entity_label: string;
  sort_order: number;
}

// Build the grouped Category → Module → (core CRUD + extras) structure from the
// server catalogue. All grouping/labels come from the data; nothing is hardcoded.
const buildCatalog = (rows: CatalogRow[]): CategoryGroup[] => {
  const cats: Record<
    string,
    { label: string; order: number; entities: Record<string, ModuleGroup & { order: number }> }
  > = {};

  for (const r of rows) {
    const catLabel = r.category || "Other";
    const cat =
      cats[catLabel] ||
      (cats[catLabel] = { label: catLabel, order: r.sort_order ?? 999, entities: {} });
    cat.order = Math.min(cat.order, r.sort_order ?? 999);

    const ent =
      cat.entities[r.entity] ||
      (cat.entities[r.entity] = {
        key: r.entity,
        label: r.entity_label || r.entity,
        core: {},
        extras: [],
        isGrid: false,
        order: r.sort_order ?? 999,
      });

    const row: PermRow = { slug: r.slug, label: r.label || r.action, description: r.description };
    if ((CORE_ACTIONS as readonly string[]).includes(r.action)) ent.core[r.action] = row;
    else ent.extras.push(row);
  }

  return Object.values(cats)
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    .map((c) => ({
      key: c.label,
      label: c.label,
      modules: Object.values(c.entities)
        .map((e) => ({
          key: e.key,
          label: e.label,
          core: e.core,
          extras: e.extras,
          isGrid: CORE_ACTIONS.every((a) => !!e.core[a]),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }));
};

const Avatar = ({ member, size = 36 }: { member: any; size?: number }) => (
  <div
    className="d-flex align-items-center justify-content-center th-font-weight-700 flex-shrink-0"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: TEAL,
      color: "#fff",
      fontSize: size * 0.36,
    }}
  >
    {initials(member)}
  </div>
);

// A single permission row. `roleHas` is the role's default; `on` is the effective
// state being edited. The gap between them is the override we surface.
const PermissionRow = ({
  row,
  on,
  roleHas,
  showRoleBaseline,
  disabled,
  onToggle,
  onResetInherit,
}: {
  row: PermRow;
  on: boolean;
  roleHas: boolean;
  showRoleBaseline: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
  onResetInherit?: () => void;
}) => {
  const overridden = showRoleBaseline && on !== roleHas;
  return (
    <div
      className="d-flex align-items-center gap-2 py-1"
      style={{ borderBottom: "1px solid #f5f5f5" }}
    >
      <Switch
        size="small"
        checked={on}
        disabled={disabled}
        onChange={onToggle}
      />
      <span style={{ flex: 1, minWidth: 0 }}>
        {row.label}
        {row.description && (
          <Tooltip title={row.description}>
            <InfoCircleOutlined
              style={{ marginLeft: 6, color: "#9aa0a6", cursor: "help" }}
            />
          </Tooltip>
        )}
      </span>
      {showRoleBaseline && !overridden && (
        <span className="th-text-grey-color th-font-size-12">
          {roleHas ? "role default" : "—"}
        </span>
      )}
      {overridden && (
        <Space size={4}>
          <Tag
            color={on ? "green" : "red"}
            style={{ marginInlineEnd: 0 }}
          >
            {on ? "+ granted" : "− revoked"}
          </Tag>
          {onResetInherit && !disabled && (
            <Button type="link" size="small" onClick={onResetInherit}>
              inherit
            </Button>
          )}
        </Space>
      )}
    </div>
  );
};

// One CRUD cell: a compact switch, plus a coloured dot when the value overrides
// the role default (green = granted, red = revoked; click the dot to inherit).
const GridCell = ({
  row,
  effective,
  roleSet,
  showRoleBaseline,
  disabled,
  onToggle,
  onResetInherit,
}: {
  row?: PermRow;
  effective: Set<string>;
  roleSet: Set<string>;
  showRoleBaseline: boolean;
  disabled: boolean;
  onToggle: (slug: string, next: boolean) => void;
  onResetInherit?: (slug: string) => void;
}) => {
  if (!row) return <span style={{ color: "#d9d9d9" }}>—</span>;
  const on = effective.has(row.slug);
  const roleHas = roleSet.has(row.slug);
  const overridden = showRoleBaseline && on !== roleHas;
  return (
    <Tooltip
      title={
        `${row.label}${row.description ? ` — ${row.description}` : ""}` +
        (overridden ? `  ·  override: ${on ? "granted" : "revoked"} (click dot to inherit)` : "")
      }
    >
      <span className="d-inline-flex align-items-center" style={{ gap: 4 }}>
        <Switch size="small" checked={on} disabled={disabled} onChange={(v) => onToggle(row.slug, v)} />
        <span
          onClick={() => overridden && !disabled && onResetInherit?.(row.slug)}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: overridden ? (on ? GREEN : RED) : "transparent",
            cursor: overridden && !disabled ? "pointer" : "default",
            display: "inline-block",
          }}
        />
      </span>
    </Tooltip>
  );
};

// The permission MATRIX: CRUD modules as rows × Create/Read/Update/Delete columns,
// each module's non-CRUD extras collapsed under an expander. Irregular modules (no
// full CRUD set) are listed below as compact toggle blocks. Shared by the member
// editor (showRoleBaseline) and the role editor.
// Bulk controls for a scope (a module or a whole category). "Grant all" turns every
// slug in scope on; in the member editor "Reset to role" sets each back to its role
// default (dropping overrides in scope), and in the role editor "Clear all" turns
// them off. Hidden when the editor is read-only.
type BulkKind = "grant" | "clear" | "role";
const BulkControls = ({
  slugs,
  showRoleBaseline,
  disabled,
  compact,
  onBulk,
}: {
  slugs: string[];
  showRoleBaseline: boolean;
  disabled: boolean;
  compact?: boolean;
  onBulk: (slugs: string[], kind: BulkKind) => void;
}) => {
  if (disabled || slugs.length === 0) return null;
  const btn = (label: string, kind: BulkKind) => (
    <Button
      type="link"
      size="small"
      style={{ padding: "0 4px", height: "auto", fontSize: 12 }}
      onClick={(e) => {
        e.stopPropagation();
        onBulk(slugs, kind);
      }}
    >
      {label}
    </Button>
  );
  return (
    <Space size={0} onClick={(e) => e.stopPropagation()}>
      {btn(compact ? "all" : "Grant all", "grant")}
      {showRoleBaseline
        ? btn(compact ? "role" : "Reset to role", "role")
        : btn(compact ? "clear" : "Clear all", "clear")}
    </Space>
  );
};

const COL = 60;
const ACTIONS_COL = 104;
const PermissionGrid = ({
  catalog,
  effective,
  roleSet,
  showRoleBaseline,
  disabled,
  term,
  onToggle,
  onResetInherit,
  onBulk,
}: {
  catalog: CategoryGroup[];
  effective: Set<string>;
  roleSet: Set<string>;
  showRoleBaseline: boolean;
  disabled: boolean;
  term: string;
  onToggle: (slug: string, next: boolean) => void;
  onResetInherit?: (slug: string) => void;
  onBulk: (slugs: string[], kind: BulkKind) => void;
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const q = term.trim().toLowerCase();

  const rowMatch = (r?: PermRow) =>
    !!r && (!q || r.slug.toLowerCase().includes(q) || r.label.toLowerCase().includes(q));
  const moduleMatch = (m: ModuleGroup) =>
    !q ||
    m.label.toLowerCase().includes(q) ||
    CORE_ACTIONS.some((a) => rowMatch(m.core[a])) ||
    m.extras.some(rowMatch);

  const wide = (r: PermRow) => (
    <PermissionRow
      key={r.slug}
      row={r}
      on={effective.has(r.slug)}
      roleHas={roleSet.has(r.slug)}
      showRoleBaseline={showRoleBaseline}
      disabled={disabled}
      onToggle={(next) => onToggle(r.slug, next)}
      onResetInherit={onResetInherit ? () => onResetInherit(r.slug) : undefined}
    />
  );

  const moduleSlugs = (m: ModuleGroup): string[] => [
    ...(CORE_ACTIONS.map((a) => m.core[a]?.slug).filter(Boolean) as string[]),
    ...m.extras.map((x) => x.slug),
  ];

  const cats = catalog.map((cat) => {
    const modules = cat.modules.filter(moduleMatch);
    return {
      ...cat,
      grid: modules.filter((m) => m.isGrid),
      list: modules.filter((m) => !m.isGrid),
    };
  }).filter((c) => c.grid.length || c.list.length);

  if (cats.length === 0)
    return <Empty className="my-4" image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching permissions" />;

  return (
    <>
      {cats.map((cat) => (
        <div key={cat.key} className="mb-4">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="th-font-weight-700 primary-color-text">{cat.label}</span>
            <BulkControls
              slugs={[...cat.grid, ...cat.list].flatMap(moduleSlugs)}
              showRoleBaseline={showRoleBaseline}
              disabled={disabled}
              onBulk={onBulk}
            />
          </div>

          {cat.grid.length > 0 && (
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, overflow: "hidden" }}>
              <div
                className="d-flex align-items-center px-2 py-1"
                style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}
              >
                <div style={{ flex: 1 }} className="th-text-grey-color th-font-size-12 th-font-weight-600">
                  Module
                </div>
                {CORE_ACTIONS.map((a) => (
                  <div
                    key={a}
                    style={{ width: COL, textAlign: "center" }}
                    className="th-text-grey-color th-font-size-12 th-font-weight-600"
                  >
                    {cap(a)}
                  </div>
                ))}
                <div style={{ width: ACTIONS_COL, flexShrink: 0 }} />
              </div>
              {cat.grid.map((m) => {
                const total = CORE_ACTIONS.filter((a) => m.core[a]).length + m.extras.length;
                const on =
                  CORE_ACTIONS.filter((a) => m.core[a] && effective.has(m.core[a]!.slug)).length +
                  m.extras.filter((x) => effective.has(x.slug)).length;
                const isOpen = !!q || expanded.has(m.key);
                const shownExtras = q ? m.extras.filter(rowMatch) : m.extras;
                return (
                  <div key={m.key} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <div className="d-flex align-items-center px-2 py-1">
                      <div style={{ flex: 1, minWidth: 0 }} className="d-flex align-items-center gap-1">
                        {m.extras.length > 0 ? (
                          <Button
                            type="text"
                            size="small"
                            style={{ padding: "0 4px", width: 22 }}
                            onClick={() =>
                              setExpanded((prev) => {
                                const s = new Set(prev);
                                s.has(m.key) ? s.delete(m.key) : s.add(m.key);
                                return s;
                              })
                            }
                          >
                            {isOpen ? "▾" : "▸"}
                          </Button>
                        ) : (
                          <span style={{ display: "inline-block", width: 22 }} />
                        )}
                        <span className="th-font-weight-600">{m.label}</span>
                        <span className="th-text-grey-color th-font-size-12">
                          {on}/{total}
                        </span>
                      </div>
                      {CORE_ACTIONS.map((a) => (
                        <div key={a} style={{ width: COL, textAlign: "center", flexShrink: 0 }}>
                          <GridCell
                            row={m.core[a]}
                            effective={effective}
                            roleSet={roleSet}
                            showRoleBaseline={showRoleBaseline}
                            disabled={disabled}
                            onToggle={onToggle}
                            onResetInherit={onResetInherit}
                          />
                        </div>
                      ))}
                      <div style={{ width: ACTIONS_COL, textAlign: "right", flexShrink: 0 }}>
                        <BulkControls
                          slugs={moduleSlugs(m)}
                          showRoleBaseline={showRoleBaseline}
                          disabled={disabled}
                          compact
                          onBulk={onBulk}
                        />
                      </div>
                    </div>
                    {isOpen && shownExtras.length > 0 && (
                      <div className="pb-2" style={{ paddingLeft: 34, paddingRight: 8, background: "#fcfcfc" }}>
                        {shownExtras.map(wide)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {cat.list.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 12,
                marginTop: cat.grid.length ? 12 : 0,
              }}
            >
              {cat.list.map((m) => {
                const rows = [
                  ...(CORE_ACTIONS.map((a) => m.core[a]).filter(Boolean) as PermRow[]),
                  ...m.extras,
                ].filter((r) => (q ? rowMatch(r) : true));
                if (rows.length === 0) return null;
                const onCount = rows.filter((r) => effective.has(r.slug)).length;
                return (
                  <div key={m.key} style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "10px 12px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="th-font-weight-600">{m.label}</span>
                      <span className="d-flex align-items-center gap-1">
                        <span className="th-text-grey-color th-font-size-12">
                          {onCount}/{rows.length}
                        </span>
                        <BulkControls
                          slugs={moduleSlugs(m)}
                          showRoleBaseline={showRoleBaseline}
                          disabled={disabled}
                          compact
                          onBulk={onBulk}
                        />
                      </span>
                    </div>
                    {rows.map(wide)}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

// Catalogue manager — create / edit / deactivate the permissions themselves. This
// is what makes the catalogue fully backend-controlled: a new permission is one
// API call, no migration and no frontend edit.
interface PermForm {
  slug: string;
  label: string;
  description: string;
  category: string;
  entity: string;
  entity_label: string;
  action: string;
}
const EMPTY_FORM: PermForm = {
  slug: "", label: "", description: "", category: "", entity: "", entity_label: "", action: "",
};

const CatalogManager = ({
  catalog,
  canEdit,
  onChanged,
}: {
  catalog: CategoryGroup[];
  canEdit: boolean;
  onChanged: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [f, setF] = useState<PermForm>(EMPTY_FORM);

  const categoryOptions = catalog.map((c) => ({ value: c.label }));
  const set = (patch: Partial<PermForm>) => setF((prev) => ({ ...prev, ...patch }));

  const openCreate = () => {
    setEditingSlug(null);
    setF(EMPTY_FORM);
    setOpen(true);
  };
  const openEdit = (row: PermRow, categoryLabel: string, entityLabel: string) => {
    setEditingSlug(row.slug);
    setF({
      ...EMPTY_FORM,
      slug: row.slug,
      label: row.label ?? "",
      description: row.description ?? "",
      category: categoryLabel,
      entity_label: entityLabel,
    });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        label: f.label,
        description: f.description,
        category: f.category || undefined,
        entity: f.entity || undefined,
        entity_label: f.entity_label || undefined,
        action: f.action || undefined,
      };
      const ok = editingSlug
        ? await updatePermission(editingSlug, payload)
        : await createPermission({ slug: f.slug.trim(), ...payload });
      if (ok) {
        setOpen(false);
        onChanged();
      }
    } finally {
      setSaving(false);
    }
  };

  const q = search.trim().toLowerCase();
  const matches = (r: PermRow) =>
    !q || r.slug.toLowerCase().includes(q) || (r.label ?? "").toLowerCase().includes(q);

  return (
    <div
      style={{
        border: "1px solid #f0f0f0",
        borderRadius: 10,
        height: "calc(100vh - 260px)",
        minHeight: 480,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center p-3"
        style={{ borderBottom: "1px solid #f0f0f0" }}
      >
        <Input.Search
          allowClear
          placeholder="Search permission or module"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button type="primary" disabled={!canEdit} onClick={openCreate}>
          Add permission
        </Button>
      </div>

      <div style={{ overflowY: "auto", flex: 1 }} className="p-3">
        {catalog.map((cat) => {
          const modules = cat.modules
            .map((m) => ({
              m,
              rows: [
                ...(CORE_ACTIONS.map((a) => m.core[a]).filter(Boolean) as PermRow[]),
                ...m.extras,
              ].filter(matches),
            }))
            .filter((x) => x.rows.length > 0);
          if (modules.length === 0) return null;
          return (
            <div key={cat.key} className="mb-3">
              <div className="th-font-weight-700 primary-color-text mb-2">{cat.label}</div>
              {modules.map(({ m, rows }) => (
                <div key={m.key} className="mb-2">
                  <div className="th-font-weight-600 th-font-size-12 th-text-grey-color mb-1">
                    {m.label}
                  </div>
                  {rows.map((r) => (
                    <div
                      key={r.slug}
                      className="d-flex align-items-center gap-2 py-1"
                      style={{ borderBottom: "1px solid #f5f5f5" }}
                    >
                      <code style={{ fontSize: 12, color: "#555", minWidth: 220 }}>{r.slug}</code>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {r.label}
                        {r.description && (
                          <Tooltip title={r.description}>
                            <InfoCircleOutlined style={{ marginLeft: 6, color: "#9aa0a6" }} />
                          </Tooltip>
                        )}
                      </span>
                      {canEdit && (
                        <Space size={4}>
                          <Button type="link" size="small" onClick={() => openEdit(r, cat.label, m.label)}>
                            Edit
                          </Button>
                          <Popconfirm
                            title="Deactivate this permission?"
                            description="It drops out of the catalogue. Existing grants are left intact."
                            okText="Deactivate"
                            okButtonProps={{ danger: true }}
                            onConfirm={async () => {
                              if (await deactivatePermission(r.slug)) onChanged();
                            }}
                          >
                            <Button type="link" size="small" danger>
                              Deactivate
                            </Button>
                          </Popconfirm>
                        </Space>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <Modal
        title={editingSlug ? `Edit permission` : "Add permission"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText={editingSlug ? "Save" : "Create"}
        okButtonProps={{ loading: saving, disabled: !canEdit || (!editingSlug && !f.slug.trim()) }}
        destroyOnClose
      >
        <div className="mb-2">
          <div className="th-font-weight-600 mb-1">Slug</div>
          <Input
            placeholder="module.action (e.g. crm.store_display)"
            value={f.slug}
            disabled={!!editingSlug}
            onChange={(e) => set({ slug: e.target.value })}
          />
          {!editingSlug && (
            <div className="th-text-grey-color th-font-size-12 mt-1">
              Lower-snake, dot-separated. Entity/action default from the slug.
            </div>
          )}
        </div>
        <div className="mb-2">
          <div className="th-font-weight-600 mb-1">Label</div>
          <Input value={f.label} onChange={(e) => set({ label: e.target.value })} />
        </div>
        <div className="mb-2">
          <div className="th-font-weight-600 mb-1">Description</div>
          <Input.TextArea
            rows={2}
            value={f.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>
        <div className="d-flex gap-2">
          <div className="mb-2" style={{ flex: 1 }}>
            <div className="th-font-weight-600 mb-1">Category</div>
            <AutoComplete
              style={{ width: "100%" }}
              options={categoryOptions}
              value={f.category}
              onChange={(v) => set({ category: v })}
              placeholder="e.g. Sales & CRM"
            />
          </div>
          <div className="mb-2" style={{ flex: 1 }}>
            <div className="th-font-weight-600 mb-1">Module (display)</div>
            <Input
              value={f.entity_label}
              onChange={(e) => set({ entity_label: e.target.value })}
              placeholder="e.g. Store Display"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const MemberPermissions = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("branch_details.update");

  const [mode, setMode] = useState<"members" | "roles" | "catalog">("members");

  // Shared: role → default slug set, lazily fetched and cached.
  const [roleCache, setRoleCache] = useState<Record<string, Set<string>>>({});
  const ensureRole = async (role: string | null): Promise<Set<string>> => {
    if (!role) return new Set();
    if (roleCache[role]) return roleCache[role];
    const slugs = (await fetchRolePermissions(role)) ?? [];
    const set = new Set(slugs);
    setRoleCache((prev) => ({ ...prev, [role]: set }));
    return set;
  };

  // ---- Members mode ----
  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [effective, setEffective] = useState<Set<string>>(new Set());
  const [baseline, setBaseline] = useState<Set<string>>(new Set());
  const [memberRoleSet, setMemberRoleSet] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState("");
  const [permSearch, setPermSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- Roles mode ----
  const [roles, setRoles] = useState<{ name: string; count: number }[]>([]);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);
  const [roleEffective, setRoleEffective] = useState<Set<string>>(new Set());
  const [roleBaseline, setRoleBaseline] = useState<Set<string>>(new Set());
  const [roleSearch, setRoleSearch] = useState(""); // left role-list filter
  const [rolePermSearch, setRolePermSearch] = useState(""); // editor perm filter
  const [roleSaving, setRoleSaving] = useState(false);
  const [enforcing, setEnforcing] = useState(false);

  // The permission catalogue — the single source the grid is built from.
  const [catalogRows, setCatalogRows] = useState<CatalogRow[]>([]);
  const catalog = useMemo(() => buildCatalog(catalogRows), [catalogRows]);
  const reloadCatalog = () =>
    fetchPermissionCatalog().then((rows) => {
      if (Array.isArray(rows)) setCatalogRows(rows as CatalogRow[]);
    });

  useEffect(() => {
    let cancelled = false;
    setLoadingMembers(true);
    fetchBranchMembers()
      .then((res) => {
        if (!cancelled) setMembers(res ?? []);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    fetchUserPermissions().then((res: any) => {
      if (cancelled || !Array.isArray(res)) return;
      setRoles(
        res
          .map((r: any) => ({ name: r?.permission_name, count: 0 }))
          .filter((r: any) => !!r.name)
      );
    });
    fetchPermissionCatalog().then((rows) => {
      if (!cancelled && Array.isArray(rows)) setCatalogRows(rows as CatalogRow[]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Member counts per role, for the role list.
  const roleCounts = useMemo(() => {
    const m: Record<string, number> = {};
    members.forEach((mem) => {
      const r = memberRole(mem);
      if (r) m[r] = (m[r] ?? 0) + 1;
    });
    return m;
  }, [members]);

  const selectedMember = useMemo(
    () => members.find((m) => m?.id === selectedId),
    [members, selectedId]
  );

  // Effective set for a member = (role ∪ granted) − revoked. Falls back to the
  // legacy flat individual_permissions when deltas are absent but a legacy list
  // exists (pre-migration environments), so the UI is correct either way.
  const computeEffective = (m: any, roleSet: Set<string>): Set<string> => {
    const granted: string[] = Array.isArray(m?.granted_permissions)
      ? m.granted_permissions
      : [];
    const revoked: string[] = Array.isArray(m?.revoked_permissions)
      ? m.revoked_permissions
      : [];
    if (
      granted.length === 0 &&
      revoked.length === 0 &&
      Array.isArray(m?.individual_permissions)
    ) {
      return new Set(m.individual_permissions);
    }
    const set = new Set(roleSet);
    granted.forEach((s) => set.add(s));
    revoked.forEach((s) => set.delete(s));
    return set;
  };

  const onSelectMember = async (id: number) => {
    setSelectedId(id);
    setPermSearch("");
    const member = members.find((m) => m?.id === id);
    const roleSet = await ensureRole(memberRole(member));
    const eff = computeEffective(member, roleSet);
    setMemberRoleSet(roleSet);
    setEffective(eff);
    setBaseline(new Set(eff));
  };

  useEffect(() => {
    if (selectedId === undefined && members.length > 0) onSelectMember(members[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members]);

  const toggleMember = (slug: string, next: boolean) =>
    setEffective((prev) => {
      const s = new Set(prev);
      next ? s.add(slug) : s.delete(slug);
      return s;
    });

  const resetInheritMember = (slug: string) =>
    setEffective((prev) => {
      const s = new Set(prev);
      memberRoleSet.has(slug) ? s.add(slug) : s.delete(slug);
      return s;
    });

  const bulkMember = (slugs: string[], kind: "grant" | "clear" | "role") =>
    setEffective((prev) => {
      const s = new Set(prev);
      slugs.forEach((slug) => {
        if (kind === "grant") s.add(slug);
        else if (kind === "clear") s.delete(slug);
        else memberRoleSet.has(slug) ? s.add(slug) : s.delete(slug); // reset to role
      });
      return s;
    });

  const memberOverrides = useMemo(() => {
    let granted = 0;
    let revoked = 0;
    effective.forEach((s) => {
      if (!memberRoleSet.has(s)) granted += 1;
    });
    memberRoleSet.forEach((s) => {
      if (!effective.has(s)) revoked += 1;
    });
    return { granted, revoked };
  }, [effective, memberRoleSet]);

  const saveMember = async () => {
    if (!selectedMember) return;
    setSaving(true);
    try {
      const granted = Array.from(effective).filter((s) => !memberRoleSet.has(s));
      const revoked = Array.from(memberRoleSet).filter((s) => !effective.has(s));
      const ok = await updateMemberDeltas(selectedMember.id, granted, revoked);
      if (ok) {
        setBaseline(new Set(effective));
        setMembers((prev) =>
          prev.map((m) =>
            m?.id === selectedMember.id
              ? {
                  ...m,
                  granted_permissions: granted,
                  revoked_permissions: revoked,
                  individual_permissions: null,
                }
              : m
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const onSelectRole = async (role: string) => {
    setSelectedRole(role);
    setRolePermSearch("");
    const set = await ensureRole(role);
    setRoleEffective(new Set(set));
    setRoleBaseline(new Set(set));
  };

  const toggleRole = (slug: string, next: boolean) =>
    setRoleEffective((prev) => {
      const s = new Set(prev);
      next ? s.add(slug) : s.delete(slug);
      return s;
    });

  const bulkRole = (slugs: string[], kind: "grant" | "clear" | "role") =>
    setRoleEffective((prev) => {
      const s = new Set(prev);
      slugs.forEach((slug) => (kind === "grant" ? s.add(slug) : s.delete(slug)));
      return s;
    });

  const saveRole = () => {
    if (!selectedRole) return;
    const count = roleCounts[selectedRole] ?? 0;
    Modal.confirm({
      title: `Update the "${roleLabel(selectedRole)}" role?`,
      content: `The permissions you changed apply to all ${count} member(s) with this role — any per-user override on those specific permissions is cleared so the change reaches everyone. Overrides on other permissions are kept.`,
      okText: "Update role",
      onOk: async () => {
        setRoleSaving(true);
        try {
          const slugs = Array.from(roleEffective);
          const ok = await updateRolePermissions(selectedRole, slugs);
          if (ok) {
            // What actually changed vs the previously-saved role set.
            const added = slugs.filter((s) => !roleBaseline.has(s));
            const removed = Array.from(roleBaseline).filter((s) => !roleEffective.has(s));
            const set = new Set(slugs);
            setRoleBaseline(new Set(set));
            setRoleCache((prev) => ({ ...prev, [selectedRole]: set }));
            // Mirror the backend's reconciliation locally so the Members tab is
            // consistent without a refetch: a newly-added slug clears members'
            // revoke of it; a newly-removed slug clears members' grant of it.
            setMembers((prev) =>
              prev.map((m) =>
                memberRole(m) === selectedRole
                  ? {
                      ...m,
                      granted_permissions: (m.granted_permissions || []).filter(
                        (s: string) => !removed.includes(s)
                      ),
                      revoked_permissions: (m.revoked_permissions || []).filter(
                        (s: string) => !added.includes(s)
                      ),
                    }
                  : m
              )
            );
          }
        } finally {
          setRoleSaving(false);
        }
      },
    });
  };

  // Force every member of the role onto its exact defaults, wiping ALL their
  // per-user overrides. Uses the SAVED role matrix, so unsaved edits must be
  // saved first (the button is disabled while the editor is dirty).
  const enforceRole = () => {
    if (!selectedRole) return;
    const count = roleCounts[selectedRole] ?? 0;
    Modal.confirm({
      title: `Reset all ${count} member(s) to the "${roleLabel(selectedRole)}" role?`,
      content:
        "This clears every individual grant/revoke override for all members of this role, so each one gets exactly the role's saved permissions. This cannot be undone.",
      okText: "Reset all members",
      okButtonProps: { danger: true },
      onOk: async () => {
        setEnforcing(true);
        try {
          const n = await enforceRoleOnMembers(selectedRole);
          if (n !== null) {
            setMembers((prev) =>
              prev.map((m) =>
                memberRole(m) === selectedRole
                  ? {
                      ...m,
                      granted_permissions: [],
                      revoked_permissions: [],
                      individual_permissions: null,
                    }
                  : m
              )
            );
          }
        } finally {
          setEnforcing(false);
        }
      },
    });
  };

  const filteredMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return members;
    const digits = query.replace(/\D/g, "");
    return members.filter((m) => {
      const contact = String(m?.user?.contact ?? "");
      return (
        memberName(m).toLowerCase().includes(query) ||
        (m?.user?.email ?? "").toLowerCase().includes(query) ||
        (roleLabel(memberRole(m)) ?? "").toLowerCase().includes(query) ||
        (!!digits && contact.replace(/\D/g, "").includes(digits))
      );
    });
  }, [members, memberSearch]);

  const memberDirty = !setsEqual(effective, baseline);
  const roleDirty = !setsEqual(roleEffective, roleBaseline);

  const shell = {
    border: "1px solid #f0f0f0",
    borderRadius: 10,
    overflow: "hidden",
    height: "calc(100vh - 260px)",
    minHeight: 480,
  } as const;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Segmented
          value={mode}
          onChange={(v) => setMode(v as "members" | "roles" | "catalog")}
          options={[
            { label: "Members", value: "members" },
            { label: "Roles", value: "roles" },
            { label: "Catalogue", value: "catalog" },
          ]}
        />
        <span className="th-text-grey-color th-font-size-12">
          {mode === "members"
            ? "Grant or revoke permissions for one person, on top of their role."
            : mode === "roles"
            ? "Edit a role's default permissions — applies to everyone with that role."
            : "Add or edit the permissions themselves — no code or migration needed."}
        </span>
      </div>

      {mode === "members" ? (
        <div className="d-flex" style={shell}>
          {/* Left: members */}
          <div
            className="flex-shrink-0 d-flex flex-column"
            style={{ width: 300, borderRight: "1px solid #f0f0f0", background: "#fafafa" }}
          >
            <div className="p-3 pb-2">
              <div className="th-font-size-16 th-font-weight-700 primary-color-text mb-1">
                Members
              </div>
              <div className="th-text-grey-color th-font-size-12 mb-2">
                {members.length} total
              </div>
              <Input.Search
                allowClear
                placeholder="Search members or contact no."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
              />
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {loadingMembers ? (
                <div className="d-flex justify-content-center py-5">
                  <Spin />
                </div>
              ) : filteredMembers.length === 0 ? (
                <Empty className="my-4" image={Empty.PRESENTED_IMAGE_SIMPLE} description="No members" />
              ) : (
                filteredMembers.map((m) => {
                  const active = m?.id === selectedId;
                  const overrides =
                    (Array.isArray(m?.granted_permissions) ? m.granted_permissions.length : 0) +
                    (Array.isArray(m?.revoked_permissions) ? m.revoked_permissions.length : 0);
                  return (
                    <div
                      key={m?.id}
                      role="button"
                      onClick={() => onSelectMember(m?.id)}
                      className="d-flex align-items-center gap-2 px-3 py-2"
                      style={{
                        cursor: "pointer",
                        borderLeft: `3px solid ${active ? TEAL : "transparent"}`,
                        background: active ? "#e6f7f9" : "transparent",
                      }}
                    >
                      <Avatar member={m} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div
                          className="th-font-weight-600"
                          style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {memberName(m)}
                        </div>
                        <div className="th-text-grey-color th-font-size-12">
                          {roleLabel(memberRole(m)) ?? "—"}
                        </div>
                      </div>
                      {overrides > 0 && (
                        <Tooltip title="Individual overrides">
                          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                            {overrides}
                          </Tag>
                        </Tooltip>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: member editor */}
          <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
            {!selectedMember ? (
              <div className="d-flex align-items-center justify-content-center flex-grow-1">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Select a member to manage their permissions"
                />
              </div>
            ) : (
              <>
                <div
                  className="d-flex align-items-center gap-3 p-3"
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <Avatar member={selectedMember} size={48} />
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="th-font-weight-700 th-font-size-16">
                      {memberName(selectedMember)}
                    </div>
                    <div className="th-text-grey-color">
                      {selectedMember?.user?.email ?? ""}
                    </div>
                  </div>
                  {memberRole(selectedMember) && (
                    <Tag color="cyan">{roleLabel(memberRole(selectedMember))}</Tag>
                  )}
                  <Tag color="blue">{effective.size} effective</Tag>
                  {memberOverrides.granted > 0 && (
                    <Tag color="green">+{memberOverrides.granted}</Tag>
                  )}
                  {memberOverrides.revoked > 0 && (
                    <Tag color="red">−{memberOverrides.revoked}</Tag>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center gap-2 p-3 flex-wrap">
                  <Input.Search
                    allowClear
                    placeholder="Search permission or module"
                    style={{ width: 260 }}
                    value={permSearch}
                    onChange={(e) => setPermSearch(e.target.value)}
                  />
                  <Button
                    size="small"
                    disabled={!canEdit || memberOverrides.granted + memberOverrides.revoked === 0}
                    onClick={() => setEffective(new Set(memberRoleSet))}
                  >
                    Reset to role
                  </Button>
                </div>

                <div style={{ overflowY: "auto", flex: 1 }} className="px-3 pb-3">
                  <PermissionGrid
                    catalog={catalog}
                    effective={effective}
                    roleSet={memberRoleSet}
                    showRoleBaseline
                    disabled={!canEdit}
                    term={permSearch}
                    onToggle={toggleMember}
                    onResetInherit={resetInheritMember}
                    onBulk={bulkMember}
                  />
                </div>

                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{ borderTop: "1px solid #f0f0f0", background: "#fff" }}
                >
                  {memberDirty ? (
                    <Tag color="orange">Unsaved changes</Tag>
                  ) : (
                    <span className="th-text-grey-color th-font-size-12">All changes saved</span>
                  )}
                  <Space>
                    <Button disabled={!memberDirty} onClick={() => setEffective(new Set(baseline))}>
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      disabled={!canEdit || !memberDirty}
                      loading={saving}
                      onClick={saveMember}
                    >
                      Save changes
                    </Button>
                  </Space>
                </div>
              </>
            )}
          </div>
        </div>
      ) : mode === "roles" ? (
        <div className="d-flex" style={shell}>
          {/* Left: roles */}
          <div
            className="flex-shrink-0 d-flex flex-column"
            style={{ width: 300, borderRight: "1px solid #f0f0f0", background: "#fafafa" }}
          >
            <div className="p-3 pb-2">
              <div className="th-font-size-16 th-font-weight-700 primary-color-text mb-1">
                Roles
              </div>
              <div className="th-text-grey-color th-font-size-12 mb-2">
                {roles.length} total
              </div>
              <Input.Search
                allowClear
                placeholder="Search roles"
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
              />
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {roles
                .filter((r) =>
                  !roleSearch.trim() ||
                  (roleLabel(r.name) ?? "").toLowerCase().includes(roleSearch.trim().toLowerCase())
                )
                .map((r) => {
                  const active = r.name === selectedRole;
                  return (
                    <div
                      key={r.name}
                      role="button"
                      onClick={() => onSelectRole(r.name)}
                      className="d-flex align-items-center justify-content-between px-3 py-2"
                      style={{
                        cursor: "pointer",
                        borderLeft: `3px solid ${active ? TEAL : "transparent"}`,
                        background: active ? "#e6f7f9" : "transparent",
                      }}
                    >
                      <span className="th-font-weight-600 text-capitalize">
                        {roleLabel(r.name)}
                      </span>
                      <Tag style={{ marginInlineEnd: 0 }}>
                        {roleCounts[r.name] ?? 0}
                      </Tag>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Right: role editor */}
          <div className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
            {!selectedRole ? (
              <div className="d-flex align-items-center justify-content-center flex-grow-1">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Select a role to edit its default permissions"
                />
              </div>
            ) : (
              <>
                <div
                  className="d-flex align-items-center gap-3 p-3"
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <div className="flex-grow-1">
                    <div className="th-font-weight-700 th-font-size-16 text-capitalize">
                      {roleLabel(selectedRole)} role
                    </div>
                    <div className="th-text-grey-color th-font-size-12">
                      Applies to {roleCounts[selectedRole] ?? 0} member(s). Individual
                      overrides are preserved on top.
                    </div>
                  </div>
                  <Tooltip
                    title={
                      roleDirty
                        ? "Save role changes first, then reset members."
                        : "Clear every member's individual overrides so they get exactly this role's permissions."
                    }
                  >
                    <Button
                      danger
                      loading={enforcing}
                      disabled={!canEdit || roleDirty || (roleCounts[selectedRole] ?? 0) === 0}
                      onClick={enforceRole}
                    >
                      Reset all members to role
                    </Button>
                  </Tooltip>
                  <Tag color="blue">{roleEffective.size} permissions</Tag>
                </div>

                <div className="p-3">
                  <Input.Search
                    allowClear
                    placeholder="Search permission or module"
                    style={{ width: 260 }}
                    value={rolePermSearch}
                    onChange={(e) => setRolePermSearch(e.target.value)}
                  />
                </div>

                <div style={{ overflowY: "auto", flex: 1 }} className="px-3 pb-3">
                  <PermissionGrid
                    catalog={catalog}
                    effective={roleEffective}
                    roleSet={roleEffective}
                    showRoleBaseline={false}
                    disabled={!canEdit}
                    term={rolePermSearch}
                    onToggle={toggleRole}
                    onBulk={bulkRole}
                  />
                </div>

                <div
                  className="d-flex justify-content-between align-items-center p-3"
                  style={{ borderTop: "1px solid #f0f0f0", background: "#fff" }}
                >
                  {roleDirty ? (
                    <Tag color="orange">Unsaved changes</Tag>
                  ) : (
                    <span className="th-text-grey-color th-font-size-12">All changes saved</span>
                  )}
                  <Space>
                    <Button disabled={!roleDirty} onClick={() => setRoleEffective(new Set(roleBaseline))}>
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      disabled={!canEdit || !roleDirty}
                      loading={roleSaving}
                      onClick={saveRole}
                    >
                      Save role
                    </Button>
                  </Space>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <CatalogManager catalog={catalog} canEdit={canEdit} onChanged={reloadCatalog} />
      )}
    </div>
  );
};

export default MemberPermissions;
